import BaseLayout from '@/layout/BaseLayout';
import Dropzone from '@shelflix/ui-web/components/Dropzone/index';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useForm } from 'react-hook-form';

import { trpc } from '@/trpcClient';
import { checkMaterialTypeEditInput, type EditMaterialTypeInputType } from '@shelflix/schemas/materialType/edit';
import Icon from '@shelflix/ui-web/components/Icon';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMaterialTypeFormStore } from './store';

import type { BrandDto } from '@shelflix/trpc/routes/brand/list/useCase';
import { RadioGroup, RadioGroupItem } from '@shelflix/ui-web/components/ui/radio-group';
import { toast } from 'sonner';

import { GenericSelect } from '@/components/GenericSelect';
import { MultiSelect } from '@/components/MultiSelect';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { logger } from '@/utils/logger';
import { usePreventLeave } from '@/utils/usePreventLeave';
import { urlToFile } from '@/utils/utils';
import { Loader2 } from 'lucide-react';

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
import { EBrandCategory } from '@shelflix/schemas/brand/enums';
import { MaterialTypeCategoryDto } from '@shelflix/trpc/routes/materialType/listCategory/useCase';
import { Textarea } from '@shelflix/ui-web/components/ui/textarea';

export function MaterialTypeEdit() {
  const { uuid: materialTypeUuid } = useParams<{ uuid: string }>();
  const { data, isLoading: isLoadingMBrands } = useQuery(
    trpc.brand.list.queryOptions(
      { recordsPerPage: 100 },
      {
        refetchOnWindowFocus: false,
      }
    )
  );

  const [isUploading, setIsUploading] = useState(false);
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());

  const brands = data?.brands ?? [];

  const { data: materialTypeToEdit, isLoading: isLoadingMaterial } = useQuery(
    trpc.materialType.get.queryOptions(
      {
        uuid: materialTypeUuid ?? '',
      },
      {
        enabled: !!materialTypeUuid && !isLoadingMBrands,
        refetchOnWindowFocus: false,
      }
    )
  );

  const { formData, resetForm } = useMaterialTypeFormStore();
  const editMaterialTypeMutation = useMutation(trpc.materialType.edit.mutationOptions());
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [currentBrand, setSelectedBrand] = useState<BrandDto | null>(null);

  const navigate = useNavigate();

  const form = useForm<
    EditMaterialTypeInputType & {
      categoryUuid: {
        uuid: string;
        name: string;
        code: string;
      };
    }
  >({
    defaultValues: { ...formData },
    mode: 'all',
  });

  useEffect(() => {
    if (materialTypeToEdit) {
      urlToFile(materialTypeToEdit.photoUrl, 'default').then((file) => {
        setThumbnail(file);

        form.reset({
          ...materialTypeToEdit,
          categoryUuid: {
            uuid: materialTypeToEdit.category.uuid,
            name: materialTypeToEdit.category.name,
            code: materialTypeToEdit.category.code,
          },
          brand: materialTypeToEdit.brand.uuid,
          multibrandBrands: materialTypeToEdit.multibrandBrands?.map((b) => b.uuid),
          fileType: file?.type ?? '',
        });
      });
      const selected = brands.find((b) => b.uuid === materialTypeToEdit.brand.uuid);
      setSelectedBrand(selected ? { ...selected } : null);

      setCategory(materialTypeToEdit.category);
    }
  }, [materialTypeToEdit]);

  useEffect(() => {
    form.trigger('fileType');
  }, [thumbnail]);

  const onSubmit = async (
    data: EditMaterialTypeInputType & {
      categoryUuid: {
        uuid: string;
        name: string;
        code: string;
      };
    }
  ) => {
    if (editMaterialTypeMutation.isPending || uploadImageMutation.isPending) return;

    try {
      setIsUploading(true);

      // First, upload the image with compression if there's a new thumbnail
      let imageKey: string | undefined;
      if (thumbnail) {
        const base64Data = await fileToBase64(thumbnail);
        const uploadResult = await uploadImageMutation.mutateAsync({
          base64Data,
          filename: thumbnail.name,
          imageType: 'material-type-photo',
        });
        imageKey = uploadResult.key;
        logger.info('Image uploaded', {
          originalSize: uploadResult.originalSize,
          compressedSize: uploadResult.compressedSize,
          savingsPercent: uploadResult.savingsPercent,
        });
      }

      // Only send fields expected by the schema - exclude extra fields like category, photoUrl
      // Also exclude null/undefined optional fields as TypeBox doesn't accept null for optional fields
      const payload: EditMaterialTypeInputType = {
        uuid: data.uuid,
        name: data.name,
        brand: data.brand,
        categoryUuid: typeof data.categoryUuid === 'string' ? data.categoryUuid : data.categoryUuid.uuid,
        active: data.active,
        ...(imageKey && { imageKey }), // Use pre-uploaded key if available
        ...(data.observation && { observation: data.observation }),
        ...(data.multibrandBrands?.length && { multibrandBrands: data.multibrandBrands }),
      };

      await editMaterialTypeMutation.mutateAsync(payload);
      resetForm();
      navigate('/material', { state: { toastSuccess: true } });
    } catch (error) {
      toast.error(`Erro ao editar material: ${(error as Error).message}`);
    } finally {
      setIsUploading(false);
    }
  };

  usePreventLeave(isUploading);

  const dropzoneRef = useRef<HTMLDivElement>(null);

  const multibrandBrands = form.watch('multibrandBrands');

  const [category, setCategory] = useState<MaterialTypeCategoryDto | null>(null);

  const { data: categorys } = useQuery({
    ...trpc.materialType.listCategory.queryOptions({}),
    refetchOnWindowFocus: false,
  });

  return (
    <BaseLayout>
      {isLoadingMaterial || isLoadingMBrands ? (
        <div className="inset-0 z-50 flex flex-1 flex-col items-center justify-center gap-4">
          <Loader2 className="text-primary h-16 w-16 animate-spin" />
          <span>Aguarde...</span>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col gap-4">
              <Card>
                <CardHeader className="pb-0">
                  <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                    Informações de Material
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex w-full flex-col gap-10 rounded-[5px] sm:flex-row">
                  <div className="flex flex-col sm:w-1/4">
                    <FormField
                      control={form.control}
                      name="fileType"
                      rules={{ validate: (value) => checkMaterialTypeEditInput('fileType', value) }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-[3px]">
                          <FormControl>
                            <Dropzone
                              removable={false}
                              onFilesSelected={(files) => {
                                setThumbnail(files[0]);
                                field.onChange(files[0]?.type);
                              }}
                              ref={dropzoneRef}
                              title="Arraste ou clique aqui para selecionar seu arquivo"
                              description="JPG, PNG ou SVG com fundo transparente (1:1)"
                              icon={
                                <Icon
                                  size={44}
                                  className="h-12 w-12 text-[var(--gray-secondary)]"
                                  name="image"
                                  fill={1}
                                />
                              }
                              requiredAspectRatio="1:1"
                              variant="square"
                              value={thumbnail ? [thumbnail] : undefined}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-5 sm:w-4/4">
                    <div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-12">
                      <FormField
                        control={form.control}
                        name="name"
                        rules={{ validate: (value) => checkMaterialTypeEditInput('name', value) }}
                        render={({ field }) => (
                          <FormItem className="flex flex-grow flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Nome do Material
                            </FormLabel>
                            <FormControl>
                              <Input className="h-10" placeholder="Digite o nome da material" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="active"
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-2.5">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Status</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={(value) => field.onChange(value === 'true')}
                                defaultValue={String(field.value)}
                                className="flex gap-6"
                                value={String(field.value)}
                              >
                                <FormItem className="flex items-center space-y-0 space-x-2">
                                  <FormControl>
                                    <RadioGroupItem value={'true'} id="ativo" />
                                  </FormControl>
                                  <FormLabel
                                    htmlFor="ativo"
                                    className={
                                      field.value === true
                                        ? 'text-primary text-sm font-semibold'
                                        : 'text-sm text-[#6d6d6d]'
                                    }
                                  >
                                    Ativo
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-y-0 space-x-2">
                                  <FormControl>
                                    <RadioGroupItem value="false" id="inativo" />
                                  </FormControl>
                                  <FormLabel
                                    htmlFor="inativo"
                                    className={
                                      field.value === false
                                        ? 'text-primary text-sm font-semibold'
                                        : 'text-sm text-[#6d6d6d]'
                                    }
                                  >
                                    Inativo
                                  </FormLabel>
                                </FormItem>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="flex flex-col gap-4 sm:flex-row">
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="brand"
                          rules={{ validate: (value) => checkMaterialTypeEditInput('brand', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Marca Vinculada
                              </FormLabel>
                              <FormControl>
                                <Select
                                  defaultValue={field.value ?? undefined}
                                  value={field.value ?? undefined}
                                  onValueChange={(brandUuid) => {
                                    field.onChange(brandUuid);
                                    const newBrand = brands.find((b) => b.uuid === brandUuid) ?? null;
                                    setSelectedBrand(newBrand);
                                    form.setValue('multibrandBrands', []);

                                    if (newBrand?.relatedBrands?.length) {
                                      const relatedUuids = newBrand.relatedBrands.map((b) => b.uuid);
                                      form.setValue('multibrandBrands', relatedUuids);
                                    }
                                  }}
                                >
                                  <SelectTrigger className="h-10 w-full">
                                    <SelectValue placeholder="Selecione uma opção" />
                                  </SelectTrigger>
                                  <SelectContent className=" ">
                                    {brands.map((brand) => (
                                      <SelectItem key={brand.uuid} value={brand.uuid}>
                                        {brand.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="categoryUuid"
                          rules={{
                            validate: (value) => checkMaterialTypeEditInput('categoryUuid', value?.uuid ?? value),
                          }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Categoria de projeto
                              </FormLabel>
                              <FormControl>
                                <GenericSelect<MaterialTypeCategoryDto>
                                  onSelect={(category) => {
                                    field.onChange(category.uuid ?? category);
                                    setCategory(category);
                                  }}
                                  value={field.value}
                                  fetchItems={async (search) => {
                                    return !search
                                      ? Promise.resolve(categorys)
                                      : Promise.resolve(
                                          categorys.filter((d) => d.name.toLowerCase().includes(search.toLowerCase()))
                                        );
                                  }}
                                  renderItem={(category) => (
                                    <div>
                                      <div className="text-sm font-medium">{category.name}</div>
                                      <div className="text-muted-foreground text-xs">{category.code}</div>
                                    </div>
                                  )}
                                  getItemLabel={(brand) => brand.name}
                                  getItemKey={(category) => category.uuid}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="flex flex-col gap-1 sm:w-1/3">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                          Identificador de 3 dígitos
                        </FormLabel>
                        <Input className=" " placeholder="Marca" disabled value={category?.code} />
                      </div>
                    </div>

                    <FormField
                      control={form.control}
                      name="observation"
                      rules={{ validate: (value) => checkMaterialTypeEditInput('observation', value) }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Observações</FormLabel>
                          <FormControl>
                            <Textarea className="h-10" {...field} onInput={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
              {currentBrand?.category === EBrandCategory.MULTIBRAND && (
                <Card>
                  <CardHeader className="pb-0">
                    <CardTitle className="text-primary mb-2 text-2xl leading-5 font-semibold">Multimarca</CardTitle>
                    <CardDescription className="text-black">
                      Selecione múltiplas marcas para vinculá-las ao Material
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex w-full flex-row gap-10 rounded-[5px]">
                    <div className="flex w-full flex-col">
                      <FormField
                        control={form.control}
                        name="multibrandBrands"
                        rules={{ validate: (value) => checkMaterialTypeEditInput('multibrandBrands', value) }}
                        render={({ field }) => (
                          <FormItem className="flex w-full flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Marcas Vinculadas
                            </FormLabel>
                            <FormControl>
                              <MultiSelect
                                items={
                                  currentBrand?.relatedBrands
                                    ?.filter((brand) => !multibrandBrands?.includes(brand.uuid))
                                    .map((brand) => ({ text: brand.name, value: brand.uuid })) ?? []
                                }
                                initialValue={
                                  materialTypeToEdit.multibrandBrands
                                    ?.map((mulitBrand) => {
                                      const brand = currentBrand?.relatedBrands?.find(
                                        (b) => b.uuid === mulitBrand.uuid
                                      );
                                      return brand ? { value: brand.uuid, text: brand.name } : null;
                                    })
                                    .filter(Boolean) as { value: string; text: string }[]
                                }
                                onSelect={(item) => field.onChange([...(multibrandBrands ?? []), item.value])}
                                onRemove={(item) =>
                                  field.onChange(multibrandBrands?.filter((i) => i !== item.value) ?? [])
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            <div className="my-5 flex grow-0 justify-center">
              <RestrictedComponent
                requiredScopes={['admin:material_type:manage']}
                behavior="disable"
                showToast
                toastMessage="Você não tem permissão para editar materiais institucionais"
              >
                <Button
                  type="submit"
                  loading={editMaterialTypeMutation.isPending || isUploading}
                  className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-auto sm:w-fit sm:grow-0"
                >
                  <Icon name="label" fill={1} color="#fff" size={16} />
                  <span className="[font-family:'Figtree'] text-sm">Salvar material</span>
                </Button>
              </RestrictedComponent>
            </div>
          </form>
        </Form>
      )}
    </BaseLayout>
  );
}
